import { CtySpinner } from '@circuitry-ai/doc-ui';
import { Suspense, lazy } from 'react';

const CHUNK_RELOAD_KEY = '__cty_chunk_reload_once__';

const isChunkLoadError = (error: unknown): boolean => {
  const message = String((error as any)?.message ?? '');
  return (
    (error as any)?.name === 'ChunkLoadError' ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Failed to load module script')
  );
};

function lazyWithRetry(importFunction: () => Promise<any>) {
  const loadComponent = async () => {
    let retries = 3;

    const retry = async () => {
      try {
        const module = await importFunction();
        globalThis.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        return module;
      } catch (error) {
        if (retries > 0) {
          retries -= 1;
          // Add delay between retries (1 second)
          await new Promise(resolve => setTimeout(resolve, 1000));
          return retry();
        }
        throw error;
      }
    };

    return retry();
  };
  return lazy(loadComponent);


}

const FallbackComponent = () => <div className='flex items-center justify-center h-full'>{'Error Loading Component'}</div>;
const withChunkRecovery = (error: unknown) => {
  console.error(error);
  if (isChunkLoadError(error)) {
    const hasReloaded = globalThis.sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
    if (!hasReloaded) {
      globalThis.sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      globalThis.location.reload();
    }
  }
  return { default: FallbackComponent };
};

const AdvisorListLazy = lazyWithRetry(() => import('@circuitry-ai/doc-viewadvisor').then(module => ({ default: module.AdvisorList }))
  .catch(error => {
    return withChunkRecovery(error);
  }));

const DashboardLazy = lazyWithRetry(() => import('@circuitry-ai/doc-dashboard').then(module => ({ default: module.Dashboard }))
  .catch(error => {
    return withChunkRecovery(error);
  }));

const SettingsLazy = lazyWithRetry(() => import('@circuitry-ai/doc-settings').then(module => ({ default: module.Settings }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const SchedulerSettingsLazy = lazyWithRetry(() => import('@circuitry-ai/doc-settings').then(module => ({ default: module.SchedulerSettings }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const ConversationHistoryLazy = lazyWithRetry(() => import('@circuitry-ai/doc-conversationhistory').then(module => ({ default: module.ConversationHistory }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const ConversationMessagesLazy = lazyWithRetry(() => import('@circuitry-ai/doc-conversationhistory').then(module => ({ default: module.ConversationMessages }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const TriageLazy = lazyWithRetry(() => import('@circuitry-ai/doc-conversationhistory').then(module => ({ default: module.Triage }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const KnowledgeLazy = lazyWithRetry(() => import('@circuitry-ai/doc-knowledge').then(module => ({ default: module.Knowledge }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AdvisorKnowledgeLazy = lazyWithRetry(() => import('@circuitry-ai/doc-knowledge').then(module => ({ default: module.Advisorknowledge }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const MetadataSettingsLazy = lazyWithRetry(() => import('@circuitry-ai/doc-knowledge').then(module => ({ default: module.MetadataSettings }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const DomainDictionaryLazy = lazyWithRetry(() => import('@circuitry-ai/doc-knowledge').then(module => ({ default: module.DomainDictionary }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const GuidanceLazy = lazyWithRetry(() => import('@circuitry-ai/doc-knowledge').then(module => ({ default: module.Guidance }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AdvisorViewLazy = lazyWithRetry(() => import('@circuitry-ai/doc-viewadvisor').then(module => ({ default: module.Advisorview }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AnalyticsDetailsLazy = lazyWithRetry(() => import('@circuitry-ai/doc-analyst').then(module => ({ default: module.AnalyticsDetails }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AnalyticsListLazy = lazyWithRetry(() => import('@circuitry-ai/doc-analyst').then(module => ({ default: module.AnalyticsList }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AnalystBoardLazy = lazy(() => import('@circuitry-ai/doc-analyst').then(module => ({ default: module.AnalystBoard }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AgentsLazy = lazyWithRetry(() => import('@circuitry-ai/doc-agent').then(module => ({ default: module.Agents }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AIWorkerListLazy = lazyWithRetry(() => import('@circuitry-ai/doc-aiworker').then(module => ({ default: module.AiworkersList }))
  .catch(error => {
    return withChunkRecovery(error);
  }));
const AIWorkerViewLazy = lazyWithRetry(() => import('@circuitry-ai/doc-aiworker').then(module => ({ default: module.Aiworkers }))
  .catch(error => {
    return withChunkRecovery(error);
  }));

const AdvisorList = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AdvisorListLazy {...props} />
  </Suspense>
);
const AdvisorView = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AdvisorViewLazy {...props} />
  </Suspense>
);
const Dashboard = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <DashboardLazy {...props} />
  </Suspense>
);
const Settings = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <SettingsLazy {...props} />
  </Suspense>
);
const SchedulerSettings = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <SchedulerSettingsLazy {...props} />
  </Suspense>
);
const ConversationHistory = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <ConversationHistoryLazy {...props} />
  </Suspense>
);
const ConversationMessages = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <ConversationMessagesLazy {...props} />
  </Suspense>
);
const Triage = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <TriageLazy {...props} />
  </Suspense>
);
const Knowledge = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <KnowledgeLazy {...props} />
  </Suspense>
);
const Advisorknowledge = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AdvisorKnowledgeLazy {...props} />
  </Suspense>
);
const MetadataSettings = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <MetadataSettingsLazy {...props} />
  </Suspense>
);
const DomainDictionary = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <DomainDictionaryLazy {...props} />
  </Suspense>
);
const Guidance = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <GuidanceLazy {...props} />
  </Suspense>
);
const AnalyticsDetails = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AnalyticsDetailsLazy {...props} />
  </Suspense>
);
const AnalystBoard = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AnalystBoardLazy {...props} />
  </Suspense>
);
const AnalyticsList = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AnalyticsListLazy {...props} />
  </Suspense>
);

const Agents = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AgentsLazy {...props} />
  </Suspense>
);

const AIWorkerList = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AIWorkerListLazy {...props} />
  </Suspense>
);
const AIWorkerView = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <AIWorkerViewLazy {...props} />
  </Suspense>
);
const WorkflowBuilderLazy = lazyWithRetry(() => import('../../pages/WorkflowBuilder')
  .then(module => ({ default: module.WorkflowBuilder }))
  .catch(error => {
    return withChunkRecovery(error);
  }));

const WorkflowBuilder = (props: any) => (
  <Suspense fallback={<CtySpinner size='lg' />}>
    <WorkflowBuilderLazy {...props} />
  </Suspense>
);

export {
  AdvisorList, Dashboard, Settings, ConversationHistory, ConversationMessages,
  Knowledge, Advisorknowledge, AdvisorView, AnalyticsDetails, AnalyticsList,
  Agents, AnalystBoard, AIWorkerList, AIWorkerView, Triage, WorkflowBuilder, SchedulerSettings,
  MetadataSettings, DomainDictionary, Guidance, lazyWithRetry, withChunkRecovery,
};